import authHelper from 'Utils/Helper/AuthHelpers';
import { baseUrlFor, isPrimaryUrl } from './servers';
import { notifyUnauthorized } from './session';

/**
 * Small client for the HML Laravel API.
 *
 *  - Admin calls authenticate with the login token; partner calls with the
 *    partner's API key (that is how the backend's partner routes work).
 *  - The API answers most failures with HTTP 200 and `status: false`, so the
 *    envelope is checked as well as the HTTP code.
 *  - A 401 from the MTN (login) server signs the user out once (the session has
 *    expired or been revoked). A 401 from another server is retried once after
 *    a short wait, because a new login's token reaches the Airtel servers a
 *    second or two after sign-in; if it persists it is shown as an error on
 *    the page instead of signing the user out.
 */

/** Wait before retrying a 401 from a non-login server (ms; tests set 0). */
export const retryConfig = { tokenCopyMs: 2500 };

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(t);
        const e = new Error('Aborted');
        e.name = 'AbortError';
        reject(e);
      });
    }
  });

export class ApiError extends Error {
  constructor(message, { status, payload } = {}) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

function bearerFor(auth) {
  if (auth === 'apiKey') return authHelper.getUserInfo()?.api_key;
  return authHelper.getToken();
}

function toQuery(params) {
  if (!params) return '';
  const parts = [];
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  });
  return parts.length ? `?${parts.join('&')}` : '';
}

/** Which credential a path needs: partner routes use the API key. */
export function authFor(path) {
  return path.startsWith('/partner/') || path.startsWith('/product') ? 'apiKey' : 'token';
}

async function send(path, { method = 'GET', params, body, signal, raw = false } = {}) {
  const headers = { Accept: raw ? '*/*' : 'application/json' };
  const bearer = bearerFor(authFor(path));
  if (bearer) headers.Authorization = `Bearer ${bearer}`;

  const init = { method, headers, signal };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const url = `${baseUrlFor(path)}${path}${toQuery(params)}`;
  const primary = isPrimaryUrl(url);
  let response;
  for (let attempt = 0; ; attempt += 1) {
    try {
      response = await fetch(url, init);
    } catch (e) {
      if (e.name === 'AbortError') throw e;
      throw new ApiError("Couldn't reach the server. Check your connection and try again.", { status: 0 });
    }
    if (response.status !== 401 || primary || attempt >= 1) break;
    await sleep(retryConfig.tokenCopyMs, signal);
  }

  if (response.status === 401) {
    if (primary) {
      notifyUnauthorized();
      throw new ApiError('Your session has ended. Sign in again to continue.', { status: 401 });
    }
    throw new ApiError(
      "The Airtel & 9mobile report server didn't accept your sign-in yet. Try again in a minute; if it keeps happening, sign out and back in.",
      { status: 401 }
    );
  }

  if (raw) {
    if (!response.ok) {
      let payload;
      try {
        payload = await response.json();
      } catch (e) {
        /* not json */
      }
      throw new ApiError(payload?.message || `The server returned an error (${response.status}).`, {
        status: response.status,
        payload,
      });
    }
    return response;
  }

  let payload;
  try {
    payload = await response.json();
  } catch (e) {
    throw new ApiError(`The server returned an unexpected response (${response.status}).`, {
      status: response.status,
    });
  }

  if (!response.ok || payload?.status === false) {
    throw new ApiError(payload?.message || `The server returned an error (${response.status}).`, {
      status: response.status,
      payload,
    });
  }

  return payload;
}

export const api = {
  get: (path, params, opts) => send(path, { ...opts, params }),
  post: (path, body, opts) => send(path, { ...opts, method: 'POST', body }),

  /** Downloads a file response (CSV export) and saves it in the browser. */
  async download(path, params, fallbackName = 'export.csv') {
    const response = await send(path, { params, raw: true });
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    saveBlob(blob, match ? decodeURIComponent(match[1]) : fallbackName);
  },
};

export function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
